import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'ask-user-question' as const;

const optionLabelZod = z.string().min(1).max(40);
const optionDescriptionZod = z.string().max(120).optional();

const uniqueLabelsRefine = (
  opts: Array<{label: string}>,
  ctx: z.RefinementCtx
) => {
  if (new Set(opts.map((o) => o.label)).size !== opts.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Option labels must be unique.',
    });
  }
};

const optionZod = z.object({
  label: optionLabelZod,
  description: optionDescriptionZod,
});

const argumentZod = z.object({
  question: z
    .string()
    .min(1)
    .describe('The clarifying question to display to the user.'),
  reason: z
    .string()
    .min(1)
    .describe(
      'Why you need to ask (1 sentence, used for telemetry & debugging).'
    ),
  options: z
    .array(optionZod)
    .min(2)
    .max(4)
    .superRefine(uniqueLabelsRefine)
    .optional()
    .describe(
      'Optional 2-4 multi-choice options. OMIT to render a Yes/No question. Labels must be unique within the array. DO NOT include "Other" / "Something else" / "None of the above" — the UI auto-appends an Other affordance with a free-form text input. Use description when labels alone are ambiguous.'
    ),
  multiSelect: z
    .boolean()
    .optional()
    .describe('If true, the user can select multiple options.'),
});

export const askUserQuestionManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Ask user',
  prettyStatus: {
    executing: 'Waiting for your answer',
    executed: 'Got your answer',
    failed: 'Question canceled',
  },
  llmTool: {
    name: toolName,
    description: `
Ask the user a structured clarifying question that renders as a Yes/No or multi-choice UI in chat. Use BEFORE proceeding when the request is ambiguous, a required parameter is missing, or you are about to perform a destructive or sweeping change. Detailed policy on when to call this tool — and when to suppress your instinct to chat-ask — lives in the Human-in-the-Loop section of the system prompt.

QUESTION TEXT — write ONLY the question itself. No preamble, no warmth, no closing flourish. The card already presents it as a question from the assistant; conversational padding makes the card bloat and looks awkward.
  ✗ "I'd love to help you build something wonderful! What would you like to focus on first?"
  ✗ "Sure, I can help with that. Which button do you want to resize?"
  ✓ "What would you like to build first?"
  ✓ "Which button do you want to resize?"
Aim for one sentence ending in "?".

ARGUMENTS:
  - question: the single-sentence question as described above.
  - reason: one engineering-tone sentence explaining why you are asking. Used for telemetry and prompt tuning, not shown to the user.
  - options: OMIT for Yes/No (default labels: "Yes", "No"). Provide 2–4 options for multi-choice; each label ≤40 chars. Use the optional \`description\` (≤120 chars) only when labels alone are ambiguous.
    DO NOT add a literal "Other" / "Something else" / "None of the above" option — the UI auto-appends an "Other" affordance with a free-form text input. Filling that role yourself duplicates the button and the duplicate gets stripped client-side anyway.
    ✗ options: [{label: "Blog"}, {label: "Portfolio"}, {label: "Something else"}]
    ✓ options: [{label: "Blog"}, {label: "Portfolio"}]   // UI appends Other automatically
    Ask exactly ONE clarifying question. Pack the important choice into clear, mutually exclusive options so the answer unlocks generation. Do not plan a second follow-up question.
  - multiSelect: true if the user can pick more than one option. Defaults to false.

RESPONSE:
  - Success: { selectedLabels: string[], freeFormAnswer?: string }.
    selectedLabels is the chosen label(s); when the user picks Other, selectedLabels === ["Other"] and freeFormAnswer holds their typed text.
  - Cancel: an error tool_result. Do not re-issue the same question — see the system prompt for the next move.
    `.trim(),
    inputSchema: argumentZod,
  },
  type: 'designer',
  featureGate: FEATURE_FLAGS.AIA_STYLE_CLEANUP_ARTIFACTS,
  confirmBeforeExecuting: false,
});
